import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getCurrentUserId } from "features/authSlice";
import {
  selectIsNativeAppBannerDismissed,
  setNativeAppBannerDismissed,
} from "features/nativeAppPromoSlice";
import {
  useGetAdEligibilityQuery,
  useUpdateAdEligibilityMutation,
} from "pages/authentication/userService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { NATIVE_APP_AD_ELIGIBILITY_KEY, NATIVE_APP_BANNER_DISMISS_KEY } from "utilities/Constant";

function readCachedAdEligibility(): boolean | undefined {
  try {
    const raw = sessionStorage.getItem(NATIVE_APP_AD_ELIGIBILITY_KEY);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return undefined;
  } catch {
    return undefined;
  }
}

export const useNativeAppPromoVisibility = () => {
  const dispatch = useDispatch();
  const userId = useSelector(getCurrentUserId);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const bannerDismissed = useSelector(selectIsNativeAppBannerDismissed);
  const cachedAdEligibility = readCachedAdEligibility();
  const skipAdEligibilityGet = Boolean(userId) && cachedAdEligibility === true;
  const { data: adEligibilityFromQuery, isSuccess } = useGetAdEligibilityQuery(userId, {
    skip: !userId || skipAdEligibilityGet,
  });
  const [updateAdEligibility] = useUpdateAdEligibilityMutation();

  useEffect(() => {
    if (!userId || skipAdEligibilityGet) return;
    if (!isSuccess) return;
    if (adEligibilityFromQuery === undefined) return;

    if (cachedAdEligibility !== true && adEligibilityFromQuery === true) {
      void updateAdEligibility({ userId, eligible: false });
    }
    sessionStorage.setItem(
      NATIVE_APP_AD_ELIGIBILITY_KEY,
      adEligibilityFromQuery ? "true" : "false"
    );
  }, [userId, skipAdEligibilityGet, isSuccess, adEligibilityFromQuery, cachedAdEligibility, updateAdEligibility]);

  const adEligibility = adEligibilityFromQuery ?? cachedAdEligibility;

  const isFlagOn = Boolean(
    (featureFlag as unknown as Record<string, unknown> | undefined)?.nativeAppPromoEnabled
  );
  const isAdEligible = adEligibility === true;

  const isPromoCardVisible = useMemo(
    () => isFlagOn && isAdEligible,
    [isFlagOn, isAdEligible]
  );

  const isBannerVisible = useMemo(
    () => isFlagOn && isAdEligible && !bannerDismissed,
    [isFlagOn, isAdEligible, bannerDismissed]
  );

  const dismissBanner = useCallback(() => {
    sessionStorage.setItem(NATIVE_APP_BANNER_DISMISS_KEY, "true");
    dispatch(setNativeAppBannerDismissed(true));

  }, [dispatch]);

  return { isPromoCardVisible, isBannerVisible, dismissBanner };
};
